import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminPresets } from '../../lib/useAdminPresets';
import { ArrowLeft, Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { logger } from '../../utils/logger';
import { caymanToday } from '../../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Credit/Debit Card' },
];

// Pre-populated services — kept aligned with CreateQuote.jsx / CreateInvoice.jsx
const PRESET_SERVICES_FALLBACK = {
  "Consultation & Documentation": [
    { name: 'Travel Consultation (30 min)', price: 50 },
    { name: 'Travel Consultation (Additional Hour)', price: 80 },
    { name: 'In-Home Health Certificate', price: 0, note: 'Price varies by destination' },
    { name: 'Cayman Islands Import Permit & Compliance Service (First Pet)', price: 350 },
    { name: 'Cayman Islands Import Permit & Compliance Service (Second Pet)', price: 300 },
    { name: 'Cayman Islands Import Permit & Compliance Service (Third Pet)', price: 250 },
    { name: 'Export Permit Services (First Pet)', price: 500 },
    { name: 'Export Permit Services (Second Pet)', price: 450 },
    { name: 'Export Permit Services (Third Pet)', price: 400 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (First Pet)', price: 85 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (Second Pet)', price: 65 },
    { name: 'Export Permit Endorsement Coordination and Document Collection (Third Pet)', price: 45 },
    { name: 'Pet Passport Application', price: 250 },
    { name: 'IATA Crate/Carrier Compliance Guidance Service', price: 50 },
    { name: 'In-Home Pet Sizing & Measurement Service', price: 75 },
    { name: 'Pet Sizing & IATA Crate/Carrier Compliance Bundle', price: 100 },
    { name: 'Cayman Dog Licensing Convenience Service', price: 75 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (First Pet)', price: 390 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (Second Pet)', price: 340 },
    { name: 'Cayman Islands Import Permit + Dog Licence Bundle (Third Pet)', price: 290 },
    { name: 'Document Delivery Service', price: 0, note: 'Bespoke quotation — based on destination district' }
  ],
  "Travel & Logistics": [
    { name: 'Export Cargo Services', price: 250 },
    { name: 'Flight Arrangements and Airline Coordination Service (First Pet)', price: 175 },
    { name: 'Flight Arrangements and Airline Coordination Service (Additional)', price: 85 },
    { name: 'International Broker Coordination Service (First Pet)', price: 175 },
    { name: 'International Broker Coordination Service (Additional)', price: 85 },
    { name: 'Brokerage Travel Services', price: 0, note: 'Bespoke quotation' },
    { name: 'Cargo & Customs Pre-Clearance (First Pet)', price: 300 },
    { name: 'Cargo & Customs Pre-Clearance (Second Pet)', price: 250 },
    { name: 'Cargo & Customs Pre-Clearance (Third Pet)', price: 200 },
    { name: 'Transportation Coordination Service', price: 0, note: 'Bespoke quotation — pets, kennel weight & location' },
    { name: 'Custom Crate Builder Coordination Service', price: 75 }
  ],
  "Health & Compliance": [
    { name: 'CDC Dog Import Application', price: 50 },
    { name: 'Annual Document Maintenance (First Pet)', price: 100 },
    { name: 'Annual Document Maintenance (Additional)', price: 75 },
    { name: 'Rabies Vaccination Scheduling', price: 25 },
    { name: 'Rabies Vaccination Service (First Pet)', price: 100 },
    { name: 'Rabies Vaccination Service (Additional)', price: 75 },
    { name: 'Microchip Implantation (First Pet)', price: 80 },
    { name: 'Microchip Implantation (Additional)', price: 65 }
  ],
  "Other": [
    { name: 'Custom Service', price: 0 }
  ]
};

export const CreateReceipt = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const password = sessionStorage.getItem('admin_password');
  const PRESET_SERVICES = useAdminPresets(PRESET_SERVICES_FALLBACK);
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [originalReceipt, setOriginalReceipt] = useState(null);
  const receiptCurrency = originalReceipt?.currency || 'CI$';

  const today = caymanToday(); // YYYY-MM-DD in Cayman TZ (not UTC)

  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    payment_method: 'bank_transfer',
    payment_date: today,
    receipt_date: today,
    items: [],
    notes: '',
    invoice_number: '', // optional free-text reference
    quote_balance_remaining: '', // amount still owed (blank = paid in full)
  });

  const [currentItem, setCurrentItem] = useState({
    description: '',
    quantity: 1,
    unit_price: 0,
  });

  // Inline edit state
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    if (isEditMode && id) {
      fetchReceipt();
    }
  }, [id]);

  const fetchReceipt = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${id}`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      if (response.ok) {
        const r = await response.json();
        if (r.receipt_kind === 'combined') {
          navigate(`/admin/receipts/${r.id}`, { replace: true });
          return;
        }
        setOriginalReceipt(r);
        setFormData({
          client_name: r.client_name || '',
          client_email: r.client_email || '',
          client_phone: r.client_phone || '',
          payment_method: r.payment_method || 'bank_transfer',
          payment_date: (r.payment_date || '').slice(0, 10),
          receipt_date: (r.receipt_date || '').slice(0, 10),
          items: (r.items || []).map((it, i) => ({ ...it, _key: `existing-${i}` })),
          notes: r.notes || '',
          invoice_number: r.invoice_number || '',
          quote_balance_remaining:
            r.quote_balance_remaining != null ? String(r.quote_balance_remaining) : '',
        });
      } else {
        alert('Failed to load receipt');
        navigate('/admin/receipts');
      }
    } catch (err) {
      logger.error('Failed to fetch receipt:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!currentItem.description || currentItem.unit_price <= 0) {
      setError('Please enter item description and a price greater than zero.');
      return;
    }
    setError('');
    const total = currentItem.quantity * currentItem.unit_price;
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { ...currentItem, total, _key: `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}` },
      ],
    });
    setCurrentItem({ description: '', quantity: 1, unit_price: 0 });
  };

  // Pre-fill description + price from preset dropdown
  const selectPresetService = (serviceName, servicePrice) => {
    setCurrentItem({
      ...currentItem,
      description: serviceName,
      unit_price: servicePrice,
    });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
    if (editingItem && editingItem.index === index) setEditingItem(null);
  };

  const startEdit = (index) => {
    const it = formData.items[index];
    setEditingItem({
      ...it,
      index,
      description: it.description,
      quantity: it.quantity,
      unit_price: it.unit_price,
    });
  };

  const saveEdit = () => {
    if (!editingItem) return;
    if (!editingItem.description || editingItem.unit_price <= 0) {
      setError('Please enter item description and a price greater than zero.');
      return;
    }
    setError('');
    const gross = (editingItem.quantity || 0) * (editingItem.unit_price || 0);
    const reduction = editingItem.discount_type === 'percent' ? gross * Number(editingItem.discount_value || 0) / 100 : editingItem.discount_type === 'fixed' ? Number(editingItem.discount_value || 0) : 0;
    const total = Math.round(Math.max(gross - reduction, 0) * 100) / 100;
    const updated = [...formData.items];
    updated[editingItem.index] = {
      ...updated[editingItem.index],
      description: editingItem.description,
      quantity: editingItem.quantity,
      unit_price: editingItem.unit_price,
      total,
    };
    setFormData({ ...formData, items: updated });
    setEditingItem(null);
  };

  const cancelEdit = () => setEditingItem(null);

  const itemsUnchanged = () => {
    const clean = (items) => JSON.stringify((items || []).map(({ _key, ...rest }) => rest));
    return originalReceipt && clean(originalReceipt.items) === clean(formData.items);
  };
  const calculateSubtotal = () => itemsUnchanged() && originalReceipt.subtotal != null ? Number(originalReceipt.subtotal) : formData.items.reduce((sum, it) => sum + Number(it.total || 0), 0);
  const calculateDiscount = () => {
    if (itemsUnchanged()) return Number(originalReceipt.discount_amount || 0);
    const subtotal = calculateSubtotal(), type = originalReceipt?.discount_type, value = Number(originalReceipt?.discount_value || 0);
    return Math.min(subtotal, originalReceipt?.discount_value == null ? Number(originalReceipt?.discount_amount || 0) : type === 'percent' ? subtotal * value / 100 : type === 'fixed' ? value : Number(originalReceipt?.discount_amount || 0));
  };
  const calculateTotal = () => {
    if (itemsUnchanged()) return Number(originalReceipt.total || 0);
    return Math.round((calculateSubtotal() - calculateDiscount() + Number(originalReceipt?.tax || 0)) * 100) / 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.client_name || !formData.client_email) {
      setError('Client name and email are required.');
      return;
    }
    if (formData.items.length === 0) {
      if (isEditMode) {
        // Legacy receipts may have been imported without line items. Allow the
        // admin to save the record so they can still tidy up client info /
        // notes / amount without having to reconstruct the original items.
        const proceed = window.confirm(
          'This receipt has no line items. Save it anyway? (Legacy records can be saved without items — add them any time by editing again.)'
        );
        if (!proceed) return;
      } else {
        setError('Please add at least one line item.');
        return;
      }
    }

    setSaving(true);
    try {
      // Strip client-side-only _key before sending
      const cleanItems = formData.items.map(({ _key, ...rest }) => rest);
      const balanceStr = (formData.quote_balance_remaining ?? '').toString().trim();
      const balanceNum = balanceStr === '' ? null : parseFloat(balanceStr);
      const payload = {
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone || null,
        invoice_number: formData.invoice_number || null,
        items: cleanItems,
        total: calculateTotal(),
        ...((!isEditMode || originalReceipt?.subtotal != null) && originalReceipt?.pricing_snapshot?.source_type !== 'quote' ? {
          subtotal: calculateSubtotal(), discount_type: originalReceipt?.discount_type || null,
          discount_value: originalReceipt?.discount_value ?? null, discount_label: originalReceipt?.discount_label || null,
          discount_amount: Math.round(calculateDiscount() * 100) / 100, tax: Number(originalReceipt?.tax || 0),
        } : {}),
        currency: receiptCurrency,
        payment_method: formData.payment_method,
        payment_date: formData.payment_date || today,
        receipt_date: formData.receipt_date || today,
        notes: formData.notes || null,
        quote_balance_remaining:
          balanceNum != null && isFinite(balanceNum) && balanceNum > 0 ? balanceNum : null,
      };
      if (isEditMode && itemsUnchanged()) {
        // Metadata-only edits must not submit or overwrite payment amounts.
        ['items', 'total', 'subtotal', 'discount_type', 'discount_value', 'discount_amount', 'discount_label', 'tax'].forEach((key) => delete payload[key]);
        if (Number(formData.quote_balance_remaining || 0) === Number(originalReceipt.quote_balance_remaining || 0)) delete payload.quote_balance_remaining;
      }
      if (isEditMode) {
        delete payload.currency;
        if (formData.payment_method === (originalReceipt.payment_method || 'bank_transfer')) delete payload.payment_method;
        if (formData.payment_date === (originalReceipt.payment_date?.split('T')[0] || '')) delete payload.payment_date;
      }

      const url = isEditMode
        ? `${API_URL}/api/admin/receipts/${id}`
        : `${API_URL}/api/admin/receipts`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        const receiptId = data.id || id;
        navigate(`/admin/receipts/${receiptId}`);
      } else {
        setError(typeof data.detail === 'string' ? data.detail : 'Failed to save receipt.');
      }
    } catch (err) {
      logger.error('Save receipt error:', err);
      setError('Failed to save receipt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between admin-header-row">
          <div>
            <h1 className="text-2xl font-bold text-green-600">
              {isEditMode ? 'Edit Receipt' : 'Create Receipt'}
            </h1>
            <p className="text-sm text-foreground/60">
              {isEditMode ? 'Update payment details' : 'Manually record a payment received'}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/receipts')}
            className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-2"
            data-testid="back-to-receipts-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="container mx-auto px-6 py-6 max-w-4xl space-y-6">
        {/* Client Info */}
        <div className="bg-white rounded-xl border border-border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Client Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Name *</label>
              <input
                type="text"
                required
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
              <input
                type="email"
                required
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
                data-testid="client-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
              <input
                type="text"
                value={formData.client_phone}
                onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Invoice Reference <span className="text-foreground/50 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. PV-2026-010, external ref, or leave blank"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Payment details */}
        <div className="bg-white rounded-xl border border-border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Payment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Payment Method *</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
                data-testid="payment-method-select"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Payment Date</label>
              <input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Receipt Date</label>
              <input
                type="date"
                value={formData.receipt_date}
                onChange={(e) => setFormData({ ...formData, receipt_date: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Amount still owed — turns the receipt into a deposit/partial-payment receipt */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
            <label className="block text-sm font-semibold text-amber-900" htmlFor="balance-owed-input">
              Amount Still Owed (optional)
            </label>
            <p className="text-xs text-amber-800/80">
              Leave blank if this is full and final payment. Enter an amount to mark this as a
              partial / deposit receipt — the PDF will show "DEPOSIT PAID" plus the outstanding balance.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50 text-sm">{receiptCurrency}</span>
                <input
                  id="balance-owed-input"
                  data-testid="balance-owed-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.quote_balance_remaining}
                  onChange={(e) =>
                    setFormData({ ...formData, quote_balance_remaining: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400"
                />
              </div>
              {(() => {
                const owed = parseFloat(formData.quote_balance_remaining) || 0;
                const quotePayment = originalReceipt?.pricing_view?.is_quote_payment ?? Boolean(originalReceipt?.quote_id || originalReceipt?.quote_number);
                const grand = quotePayment ? Number(originalReceipt?.pricing_view?.document_total || calculateTotal() + owed) : calculateTotal();
                if (owed <= 0) return null;
                const paid = quotePayment ? calculateTotal() : Math.max(grand - owed, 0);
                const pctPaid = grand > 0 ? Math.round((paid / grand) * 1000) / 10 : 0;
                return (
                  <div
                    className="w-full bg-white rounded-lg border border-amber-200 p-3"
                    data-testid="balance-owed-preview"
                  >
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-amber-700/80 font-semibold">
                          {quotePayment ? originalReceipt?.pricing_view?.document_total_label || 'Balance before this payment' : 'Total Invoice'}
                        </p>
                        <p className="text-sm font-bold text-foreground" data-testid="breakdown-total">
                          {receiptCurrency}{grand.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-green-700 font-semibold">
                          Amount Paid
                        </p>
                        <p className="text-sm font-bold text-green-700" data-testid="breakdown-paid">
                          {receiptCurrency}{paid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        <p className="text-[10px] text-foreground/50">{pctPaid}% paid</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-amber-800 font-semibold">
                          Remaining
                        </p>
                        <p className="text-sm font-bold text-amber-800" data-testid="breakdown-remaining">
                          {receiptCurrency}{owed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-xl border border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Service and Inventory</h2>

          {/* Preset Services dropdown — same list used on Quotes/Invoices */}
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-foreground/70 mb-2">
              Quick Add from Services:
            </p>
            <select
              data-testid="preset-service-select"
              onChange={(e) => {
                if (!e.target.value) return;
                const selected = JSON.parse(e.target.value);
                selectPresetService(selected.name, selected.price);
                e.target.value = '';
              }}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            >
              <option value="" label="Select a service..." data-testid="preset-service-placeholder" />
              {Object.entries(PRESET_SERVICES).map(([category, services]) => (
                <optgroup key={category} label={category}>
                  {services.map((service, idx) => (
                    <option key={idx} value={JSON.stringify(service)} label={`${service.name} - ${service.price > 0 ? `CI$${service.price}` : service.note}`} data-testid={`preset-service-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${idx}`} />
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Add item row */}
          <div className="grid grid-cols-12 gap-2 items-end mb-4">
            <div className="col-span-6">
              <label className="block text-xs font-medium text-foreground/70 mb-1">Description</label>
              <input
                type="text"
                value={currentItem.description}
                onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg"
                placeholder="Service or item"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-foreground/70 mb-1">Qty</label>
              <input
                type="number"
                min="1"
                value={currentItem.quantity}
                onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-border rounded-lg"
              />
            </div>
            <div className="col-span-3">
              <label className="block text-xs font-medium text-foreground/70 mb-1">Unit Price ({receiptCurrency})</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={currentItem.unit_price}
                onChange={(e) => setCurrentItem({ ...currentItem, unit_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-border rounded-lg"
              />
            </div>
            <div className="col-span-1">
              <button
                type="button"
                onClick={handleAddItem}
                className="w-full px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                data-testid="add-item-btn"
              >
                <Plus className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>

          {formData.items.length === 0 ? (
            <p className="text-sm text-foreground/60 py-4 text-center bg-gray-50 rounded-lg">
              No items yet. Add at least one item above.
            </p>
          ) : (
            <div className="space-y-2">
              {formData.items.map((it, idx) => {
                const isEditing = editingItem && editingItem.index === idx;
                if (isEditing) {
                  return (
                    <div key={it._key || `existing-${idx}`} className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                      <input
                        type="text"
                        value={editingItem.description}
                        onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                        placeholder="Description"
                      />
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          value={editingItem.quantity}
                          onChange={(e) => setEditingItem({ ...editingItem, quantity: parseFloat(e.target.value) || 0 })}
                          className="w-20 px-3 py-2 text-sm border border-border rounded-lg"
                          placeholder="Qty"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editingItem.unit_price}
                          onChange={(e) => setEditingItem({ ...editingItem, unit_price: parseFloat(e.target.value) || 0 })}
                          className="flex-1 px-3 py-2 text-sm border border-border rounded-lg"
                          placeholder={`Unit price (${receiptCurrency})`}
                        />
                        <button
                          type="button"
                          onClick={saveEdit}
                          className="px-3 py-2 bg-primary text-white rounded-lg text-sm flex items-center gap-1"
                          data-testid={`save-edit-receipt-item-${idx}`}
                        >
                          <Check className="w-4 h-4" /> Save
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="px-3 py-2 border border-border rounded-lg text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={it._key || `existing-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{it.description}</p>
                      <p className="text-xs text-foreground/60">
                        {it.quantity} × {receiptCurrency}{Number(it.unit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-foreground mr-3">
                      {receiptCurrency}{Number(it.total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <button
                      type="button"
                      onClick={() => startEdit(idx)}
                      className="p-2 text-primary hover:bg-primary/10 rounded"
                      title="Edit"
                      data-testid={`edit-receipt-item-${idx}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(idx)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Remove"
                      data-testid={`remove-item-${idx}-btn`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}

              {originalReceipt?.pricing_view?.warning && <p role="alert" className="text-sm text-amber-800" data-testid="receipt-edit-pricing-warning">{originalReceipt.pricing_view.warning}</p>}
              {(calculateDiscount() > 0 || Number(originalReceipt?.tax) > 0) && <dl className="space-y-2 border-t pt-3 text-sm" data-testid="receipt-edit-discount-summary">
                <div className="flex justify-between gap-3"><dt>Subtotal</dt><dd data-testid="receipt-edit-subtotal">{receiptCurrency}{calculateSubtotal().toFixed(2)}</dd></div>
                {calculateDiscount() > 0 && <div className="flex justify-between gap-3 text-emerald-700"><dt>Discount{originalReceipt?.discount_type === 'percent' ? ` (${originalReceipt.discount_value}%)` : ''}{originalReceipt?.discount_label ? ` — ${originalReceipt.discount_label}` : ''}</dt><dd data-testid="receipt-edit-discount">−{receiptCurrency}{calculateDiscount().toFixed(2)}</dd></div>}
                {Number(originalReceipt?.tax) > 0 && <div className="flex justify-between gap-3"><dt>Tax</dt><dd data-testid="receipt-edit-tax">{receiptCurrency}{Number(originalReceipt.tax).toFixed(2)}</dd></div>}
              </dl>}
              <div className="flex justify-end border-t border-border pt-3">
                <div className="text-right">
                  <p className="text-xs text-foreground/60">{originalReceipt?.pricing_view?.is_quote_payment ? 'Amount paid (this receipt)' : 'Total'}</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="receipt-edit-total">
                    {receiptCurrency}{calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl border border-border p-6">
          <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
          <textarea
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Optional additional details about this payment"
            lang="en-GB"
            spellCheck="true"
            autoCorrect="on"
            autoCapitalize="sentences"
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/receipts')}
            className="px-6 py-2 border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            data-testid="save-receipt-btn"
          >
            {saving ? 'Saving…' : isEditMode ? 'Update Receipt' : 'Create Receipt'}
          </button>
        </div>
      </form>
    </div>
  );
};
